import { atom } from 'jotai';
import { atomWithReset } from 'jotai/utils';
import type { Project, ProjectSortOptions } from '@/api/v4/projects.api';
import type { INumberOption, IOption } from '@/types';
import type { ISort } from '@/ui/table/table.types';

import { projectStatusOptions } from './overview/dropdowns/project-status-options';

type ProjectTeamOption = IOption;

export type ProjectForm = Omit<
  Project,
  'id' | 'createdDate' | 'creator' | 'owner'
> & {
  creator: Project['creator'] | null;
};

export const projectFormState = atom<ProjectForm>({
  name: '',
  type: null,
  timeframeStart: null,
  timeframeEnd: null,
  public: true,
  isOwnedByUser: false,
  expectedRoi: null,
  projectKpi: null,
  problemStatement: null,
  projectBudget: null,
  externalExpectedCost: null,
  internalExpectedCost: null,
  status: null,
  team: null,
  creator: null,
  listingsCount: 0,
  tags: [],
  impacts: [],
  isPinned: false,
  mostAdvancedListingStatus: null,
});

export const projectTeamOptionsState = atom<ProjectTeamOption[] | undefined>(
  undefined,
);

export const projectIdState = atom<string | null>(null);

export const listingSidebarIdState = atom<string | null>(null);

export const projectsSortState = atom<ISort<ProjectSortOptions>>({
  sort: 'lastEditedDate',
  direction: 'desc',
});

export const isProjectHeaderVisibleState = atom<boolean>(false);

export interface IRangeNumberFilterOption {
  start: IOption;
  end: IOption | null;
}

export interface IProjectFilters {
  tags: IOption[] | null;
  impacts: IOption[] | null;
  listingStages: IOption[] | null;
  status: IOption[] | null;
  createdFor: IOption[] | null;
  createdBy: INumberOption[] | null;
  createdDate: IRangeNumberFilterOption | null;
  lastEditedDate: IRangeNumberFilterOption | null;
  search: string | null;
}

interface IProjectsFilterOptions {
  tags: IOption[];
  listingStages: IOption[];
  status: IOption[];
  createdFor: IOption[];
  createdBy: INumberOption[];
  impacts: IOption[];
  createdDate: IRangeNumberFilterOption | null;
  lastEditedDate: IRangeNumberFilterOption | null;
}

const defaultProjectFiltersOptions = {
  tags: [],
  impacts: [],
  listingStages: [],
  status: projectStatusOptions,
  createdFor: [],
  createdBy: [],
  createdDate: null,
  lastEditedDate: null,
};

export function isProjectFilterKeyContainingPredefinedOptions(
  key: string,
): key is keyof IProjectsFilterOptions {
  return key in defaultProjectFiltersOptions;
}
export const projectsFilterOptionsState = atom<IProjectsFilterOptions>(
  defaultProjectFiltersOptions,
);

export const defaultAppliedProjectsFilters: IProjectFilters = {
  tags: null,
  impacts: null,
  listingStages: null,
  status: null,
  createdFor: null,
  createdBy: null,
  createdDate: null,
  lastEditedDate: null,
  search: null,
};

export const appliedProjectsFiltersState = atomWithReset<IProjectFilters>(
  defaultAppliedProjectsFilters,
);

export const selectedProjectsFiltersState = atomWithReset<IProjectFilters>(
  defaultAppliedProjectsFilters,
);
